import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import { useFormFields } from '../../util/hooks';
import axios from 'axios';
import { useAuth } from '../../util/authContext';
import { formStyles } from '../../util/styles';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Collapse from '@mui/material/Collapse';
import Alert from '@mui/material/Alert';
import MfaSetupModal from '../Account/Modals/MfaSetupModal';
import { Card, CardActions, CardContent, CircularProgress, Box, Tabs, Tab } from '@mui/material';
import { Password, Email, Key } from '@mui/icons-material';
import ReCAPTCHAModule from 'react-google-recaptcha';
const ReCAPTCHA = ReCAPTCHAModule.default || ReCAPTCHAModule;
import { startRegistration } from '@simplewebauthn/browser';

// reCAPTCHA site key - should be in env config
const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'; // test key

const SignUpForm = ({ token, verificationToken: initialVerificationToken }) => {
  const classes = formStyles();
  const [fields, handleFieldChange] = useFormFields({
    name: '',
    password: '',
  });
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [presetEmail, setPresetEmail] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [errors, setErrors] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [configureMfa, setConfigureMfa] = useState(false);
  const [allowedMfaChannels, setAllowMfaChannels] = useState([]);
  const [mfaMessage, setMfaMessage] = useState(null);
  const [mfaPromptOpen, setMfaPromptOpen] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [showMfaTokenInput, setShowMfaTokenInput] = useState(false);
  const [preferredMfaChannel, setPreferredMfaChannel] = useState(null);

  // Email verification flow state
  const [signupStep, setSignupStep] = useState('email'); // 'email' | 'verify' | 'complete'
  const [emailVerificationToken, setEmailVerificationToken] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = React.useRef(null);

  // Auth method: 0 = password, 1 = email code (passwordless), 2 = passkey
  const [authMethod, setAuthMethod] = useState(0);

  const { createSession } = useAuth();
  const [validLink, setValidLink] = useState(true);

  // Check invite token on mount
  useEffect(() => {
    const checkToken = async () => {
      try {
        let res = await axios.post('/api/validinvitetoken', {
          inviteToken: token,
        });
        if (res && res.data.error) {
          console.log(res.data.error);
          setValidLink(false);
        } else {
          setEmail(res.data.email);
          setCompany(res.data.company);
          setPresetEmail(true);
          setSignupStep('complete'); // Skip email verification for invites
        }
      } catch (error) {
        console.log(error);
        setValidLink(false);
      }
    };
    if (token) {
      checkToken();
    }
  }, [token]);

  // Auto-verify email when arriving via verification link
  useEffect(() => {
    const verifyFromLink = async () => {
      setIsLoading(true);
      setErrors([]);
      try {
        const res = await axios.post('/api/signup/validate-email', {
          token: initialVerificationToken,
        });
        if (res.data.valid) {
          setEmailVerificationToken(initialVerificationToken);
          setEmail(res.data.email);
          setSignupStep('complete');
        } else {
          setErrors([res.data.error || 'Invalid or expired verification link']);
          setSignupStep('email');
        }
      } catch (error) {
        const errorType = error.response?.data?.errorType;
        if (errorType === 'EMAIL_VERIFICATION_TOKEN_EXPIRED') {
          setErrors(['This verification link has expired. Please sign up again.']);
        } else {
          setErrors([error.response?.data?.error || 'Invalid verification link']);
        }
        setSignupStep('email');
      }
      setIsLoading(false);
    };
    if (initialVerificationToken) {
      verifyFromLink();
    }
  }, [initialVerificationToken]);

  const getUserInfo = async () => {
    setIsLoading(true);
    let tmpUserData = null;
    try {
      const result = await axios.get(
        `/api/user?${new URLSearchParams({ email: email, password: fields.password }).toString()}`
      );
      if (result && result.data) {
        tmpUserData = result.data;
        setUserInfo(result.data);
      }
    } catch (e) {
      console.error('Error while getting user info for MFA setup: ', e);
      setErrors(['Failed to get user info. Please check your password']);
    }
    setIsLoading(false);
    return tmpUserData;
  };

  const handleMfaTokenChange = (e) => {
    let tmpMfaToken = e.target.value;
    setMfaToken(tmpMfaToken);
    if (tmpMfaToken.length === 6) {
      setTimeout(() => {
        handleCompleteSignup(null, tmpMfaToken);
      }, 300);
    }
  };

  const sendSms = async (event) => {
    event.preventDefault();
    await sendMfaToken('sms');
  };

  const sendEmail = async (event) => {
    event.preventDefault();
    await sendMfaToken('email');
  };

  const sendMfaToken = async (channel) => {
    setPreferredMfaChannel(channel);
    try {
      setIsLoading(true);
      setMfaMessage('Sending...');
      let res = await axios.post('/api/mfa/send-token', {
        email: email,
        channel: channel,
      });

      console.log('Token sent status: ', res.data);
      if (res && res.data.status) {
        setMfaMessage('Token sent via ' + channel + '!');
      }
    } catch (error) {
      console.error('Failed to send MFA token via ' + channel, error);
      setMfaMessage('An error occurred.');
      setErrors(['Failed to send MFA token via ' + channel]);
    }
    setIsLoading(false);
  };

  // Step 1: Initiate email verification
  const handleInitiateSignup = async (event) => {
    if (event) event.preventDefault();
    
    const emailLower = (email || '').toLowerCase().trim();
    if (!emailLower || !emailLower.includes('@')) {
      setErrors(['Please enter a valid email address']);
      return;
    }

    if (!recaptchaToken) {
      setErrors(['Please complete the reCAPTCHA']);
      return;
    }

    setErrors([]);
    setIsLoading(true);

    try {
      const res = await axios.post('/api/signup/initiate', {
        email: emailLower,
        recaptchaToken,
      });

      if (res.data.error) {
        setErrors([res.data.error]);
      } else {
        setSignupStep('verify');
      }
    } catch (error) {
      if (error.response?.data?.errorType === 'USER_ALREADY_EXISTS') {
        setErrors(['An account with this email already exists. Please sign in instead.']);
      } else {
        setErrors([error.response?.data?.error || error.message]);
      }
      // Reset reCAPTCHA on error
      recaptchaRef.current?.reset();
      setRecaptchaToken(null);
    }

    setIsLoading(false);
  };

  // Step 2: Validate email verification code
  const handleVerifyEmail = async (event) => {
    if (event) event.preventDefault();

    if (!verificationCode || verificationCode.length < 6) {
      setErrors(['Please enter the verification code from your email']);
      return;
    }

    setErrors([]);
    setIsLoading(true);

    try {
      const res = await axios.post('/api/signup/validate-email', {
        token: verificationCode,
      });

      if (res.data.valid) {
        setEmailVerificationToken(verificationCode);
        setSignupStep('complete');
      } else {
        setErrors([res.data.error || 'Invalid verification code']);
      }
    } catch (error) {
      if (error.response?.data?.errorType === 'EMAIL_VERIFICATION_TOKEN_EXPIRED') {
        setErrors(['Verification code expired. Please request a new one.']);
        setSignupStep('email');
      } else {
        setErrors([error.response?.data?.error || error.message]);
      }
    }

    setIsLoading(false);
  };

  // Sign in after signup (for invite flow with MFA)
  const signIn = async (tmpMfaToken, tmpChannel) => {
    let payload = {
      email: email,
      password: fields.password,
    };

    if (showMfaTokenInput || tmpMfaToken || tmpChannel) {
      payload.token = tmpMfaToken || mfaToken;
      payload.channel = tmpChannel || preferredMfaChannel;
    }

    let res = null;
    setErrors([]);
    setIsLoading(true);
    try {
      res = await axios.post('/api/signin', payload);
      if (res && res.status === 200 && res.data.user) {
        await createSession(
          {
            name: res.data.user.name,
            email: res.data.user.email,
            groupsLastUpdated: res.data.user.groupsLastUpdated,
            mfaEnabled: res.data.user.mfaEnabled,
            companyRequiresMfa: res.data.user.companyRequiresMfa,
            preferredMfa: res.data.user.preferredMfa,
          },
          res.data.expires
        );
      }
    } catch (error) {
      if (error.response) {
        res = error.response;
      }

      if (res && res.data.error) {
        console.log('Error on sign-in: ', { data: res.data });
        if (res && res.data.error === 'Missing MFA token') {
          setShowMfaTokenInput(true);
          let message = 'Please provide the token.';
          switch (res.data.channel) {
            case 'authenticator':
              message += ' Check your authenticator app.';
              break;
            case 'sms':
              message += ' A text message has been sent with the token.';
              break;
            case 'email':
              message += ' An email has been sent with the token.';
              break;
          }
          setMfaMessage(message);
          setAllowMfaChannels(res.data.allowedChannels);
          setPreferredMfaChannel(res.data.channel);
        } else if (res && res.data.error === 'Invalid MFA token') {
          setErrors(['The MFA token entered is incorrect. Please try again.']);
          setMfaMessage('Token is invalid');
        }
        if (res.data.errorType === 'MFA_REQUIRED_BY_COMPANY') {
          await getUserInfo();
          setMfaPromptOpen(true);
          setConfigureMfa(true);
        } else {
          setErrors([res.data.error]);
        }
      }
    }

    setIsLoading(false);
  };

  // Step 3: Complete signup
  const handleCompleteSignup = async (event, tmpMfaToken = null, tmpChannel = null) => {
    if (event) event.preventDefault();
    let errorList = [];

    if (!fields.name || fields.name.length < 1) {
      errorList.push('Name must be populated');
    }

    if (!presetEmail && (!company || company.length < 1)) {
      errorList.push('Company must be populated');
    }

    if (!email || !email.includes('@')) {
      errorList.push('Email is not valid');
    }

    if (authMethod === 0 && (!fields.password || fields.password.length < 8)) {
      errorList.push('Password must be longer than 8 characters');
    }

    if (errorList.length > 0) {
      setErrors(errorList);
      return;
    }

    setIsLoading(true);
    try {
      let res = null;
      if (presetEmail) {
        // Invite flow
        if (userInfo === null) {
          res = await axios.put('/api/userfrominvite', {
            name: fields.name,
            email: email,
            password: fields.password,
            platform: 'web',
            inviteToken: token,
          });
        }
        await signIn(tmpMfaToken, tmpChannel);
      } else {
        // New user flow with email verification
        const signupPayload = {
          name: fields.name,
          company: company,
          email: email,
          platform: 'web',
          emailVerificationToken: emailVerificationToken,
        };
        // Only include password for password auth method
        if (authMethod === 0) {
          signupPayload.password = fields.password;
        }

        res = await axios.post('/api/signup', signupPayload);

        if (res && res.status === 200 && res.data.user) {
          const methodNames = ['Password', 'Passwordless', 'Passkey'];
          if (typeof gtag !== 'undefined') {
            gtag('event', 'sign_up', {
              method: methodNames[authMethod] || 'Password',
            });
          }

          await createSession(
            {
              name: res.data.user.name,
              email: res.data.user.email,
              groupsLastUpdated: res.data.user.groupsLastUpdated,
              mfaEnabled: res.data.user.mfaEnabled,
              companyRequiresMfa: res.data.user.companyRequiresMfa,
              preferredMfa: res.data.user.preferredMfa,
            },
            res.data.expires
          );

          // For passkey auth method, register passkey after account creation
          if (authMethod === 2) {
            try {
              const optionsRes = await axios.get('/api/webauthn/register/options');
              const attResp = await startRegistration({ optionsJSON: optionsRes.data });
              await axios.post('/api/webauthn/register/verify', {
                response: attResp,
                deviceName: 'Initial passkey',
              });
              console.log('Passkey registered during signup');
            } catch (passkeyError) {
              console.error('Passkey registration after signup failed:', passkeyError);
              // Don't block signup — user is already signed in and can add passkey later
            }
          }
        }
      }
    } catch (error) {
      console.log(error);
      let res = error.response;
      if (res && res.data && res.data.error) {
        setErrors([res.data.error]);
      } else {
        setErrors([error.message]);
      }
    }

    setIsLoading(false);
  };

  const checkboxTerms = (
    <Typography variant="body2">
      I have reviewed and agree to the terms outlined in the&nbsp;
      <a rel="noopener noreferrer" target="_blank" href="https://www.juryboxapp.com/license-agreement">
        License Agreement
      </a>{' '}
      and&nbsp;
      <a rel="noopener noreferrer" target="_blank" href="https://www.juryboxapp.com/privacy-policy">
        Privacy Policy
      </a>
    </Typography>
  );

  // Email input step (step 1)
  const renderEmailStep = () => (
    <form className={classes.form} noValidate onSubmit={handleInitiateSignup}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Collapse in={errors && errors.length > 0}>
            <Alert severity="error">
              <div>
                {errors.map((error) => (
                  <div key={error}>{error}</div>
                ))}
              </div>
            </Alert>
          </Collapse>
        </Grid>
        <Grid size={12}>
          <Typography variant="body1" gutterBottom>
            Enter your email to get started
          </Typography>
        </Grid>
        <Grid size={12}>
          <TextField
            variant="outlined"
            fullWidth
            id="email"
            name="email"
            label="Email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            InputProps={{
              classes: { root: classes.outlinedRoot },
            }}
          />
        </Grid>
        <Grid size={12}>
          <Box display="flex" justifyContent="center">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={(token) => setRecaptchaToken(token)}
              onExpired={() => setRecaptchaToken(null)}
            />
          </Box>
        </Grid>
        <Grid size={12}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            disabled={isLoading || !recaptchaToken}
            className={classes.submit}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Continue'}
          </Button>
        </Grid>
      </Grid>
      <Grid container>
        <Grid>
          <Link href="/signin" variant="body1">
            Already have an account? Sign in
          </Link>
        </Grid>
      </Grid>
    </form>
  );

  // Email verification step (step 2)
  const renderVerifyStep = () => (
    <form className={classes.form} noValidate onSubmit={handleVerifyEmail}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Collapse in={errors && errors.length > 0}>
            <Alert severity="error">
              <div>
                {errors.map((error) => (
                  <div key={error}>{error}</div>
                ))}
              </div>
            </Alert>
          </Collapse>
        </Grid>
        <Grid size={12}>
          <Alert severity="info">
            We've sent a verification code to <strong>{email}</strong>. Please check your email and follow the link to verify your address.
          </Alert>
        </Grid>
        <Grid size={12}>
          <Button
            type="button"
            fullWidth
            variant="text"
            color="primary"
            size="small"
            onClick={() => {
              setSignupStep('email');
              setErrors([]);
            }}
          >
            Use a different email
          </Button>
        </Grid>
      </Grid>
    </form>
  );

  // Complete signup step (step 3)
  const renderCompleteStep = () => (
    <form className={classes.form} noValidate onSubmit={handleCompleteSignup}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Collapse in={errors && errors.length > 0}>
            <Alert severity="error">
              <div>
                Sign Up Failed:
                <ul>
                  {errors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              </div>
            </Alert>
          </Collapse>
        </Grid>
        <Grid size={12}>
          <TextField
            autoComplete="name"
            name="name"
            variant="outlined"
            fullWidth
            id="name"
            label="Name"
            autoFocus
            value={fields.name}
            onChange={handleFieldChange}
            InputProps={{
              classes: { root: classes.outlinedRoot },
            }}
          />
        </Grid>
        {!presetEmail && (
          <Grid size={12}>
            <TextField
              autoComplete="company"
              name="company"
              variant="outlined"
              fullWidth
              id="company"
              label="Organization"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              InputProps={{
                classes: { root: classes.outlinedRoot },
              }}
            />
          </Grid>
        )}
        <Grid size={12}>
          <TextField
            variant="outlined"
            fullWidth
            id="email"
            name="email"
            label="Email"
            autoComplete="email"
            disabled={true}
            value={email}
            InputProps={{
              classes: { root: classes.outlinedRoot },
            }}
          />
        </Grid>
        <Grid size={12}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            How would you like to sign in?
          </Typography>
          <Tabs
            value={authMethod}
            onChange={(e, newValue) => setAuthMethod(newValue)}
            variant="fullWidth"
            sx={{ mb: 1 }}
          >
            <Tab icon={<Password />} label="Password" />
            <Tab icon={<Email />} label="Email Code" />
            <Tab icon={<Key />} label="Passkey" />
          </Tabs>
        </Grid>
        {authMethod === 0 && (
          <Grid size={12}>
            <TextField
              variant="outlined"
              fullWidth
              name="password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              label="Create Password"
              autoComplete="new-password"
              value={fields.password}
              onChange={handleFieldChange}
              InputProps={{
                classes: { root: classes.outlinedRoot },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      onMouseDown={(event) => event.preventDefault()}
                      size="large"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        )}
        {authMethod === 1 && (
          <Grid size={12}>
            <Alert severity="info">
              You'll sign in using a verification code sent to your email each time. No password needed.
            </Alert>
          </Grid>
        )}
        {authMethod === 2 && (
          <Grid size={12}>
            <Alert severity="info">
              After creating your account, you'll be prompted to set up a passkey (Face ID, Touch ID, or security key).
            </Alert>
          </Grid>
        )}

        {showMfaTokenInput && (
          <Card style={{ width: '100%', margin: '7px' }}>
            <CardContent>
              <Typography gutterBottom variant="h5" component="h2">
                Multi-factor Authentication
              </Typography>
              <Grid size={12}>
                {mfaMessage && <Typography variant={'body2'}>{mfaMessage}</Typography>}
                <TextField
                  variant="outlined"
                  fullWidth
                  id="token"
                  name="token"
                  label="Token"
                  autoComplete="token"
                  autoFocus
                  value={mfaToken}
                  onChange={handleMfaTokenChange}
                  InputProps={{
                    classes: { root: classes.outlinedRoot },
                  }}
                  style={{ marginTop: '7px' }}
                />
              </Grid>
            </CardContent>
            <CardActions>
              <Grid size={12}>
                <Grid container>
                  <Grid size={3}>
                    <hr />
                  </Grid>
                  <Grid size={6} style={{ textAlign: 'center' }}>
                    {' '}
                    Having trouble?{' '}
                  </Grid>
                  <Grid size={3}>
                    <hr />
                  </Grid>
                </Grid>
                {allowedMfaChannels.includes('sms') && (
                  <Button type={'button'} color={'primary'} onClick={sendSms} disabled={isLoading}>
                    {preferredMfaChannel === 'sms' ? 'Re-send SMS' : 'Send SMS'}
                  </Button>
                )}
                <Button type={'button'} color={'primary'} onClick={sendEmail} disabled={isLoading}>
                  {preferredMfaChannel === 'email' ? 'Re-send Email' : 'Send Email'}
                </Button>
              </Grid>
            </CardActions>
          </Card>
        )}

        <Grid size={12}>
          <FormControlLabel
            control={<Checkbox color="default" />}
            label={checkboxTerms}
            onChange={() => setAgreedToTerms(!agreedToTerms)}
          />
        </Grid>
        <Grid size={12}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            className={classes.submit}
            disabled={!agreedToTerms || isLoading}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Sign Up'}
          </Button>
        </Grid>
      </Grid>
      <Grid container>
        <Grid>
          <Link href="/signin" variant="body1">
            Already have an account? Sign in
          </Link>
        </Grid>
      </Grid>

      {configureMfa && userInfo !== null && (
        <MfaSetupModal
          key={'mfa-prompt-modal'}
          open={mfaPromptOpen}
          onComplete={(mfaToken, channel) => {
            setMfaPromptOpen(false);
            setShowMfaTokenInput(true);

            if (channel && channel === 'email' && !mfaToken) {
              sendMfaToken('email');
            }

            if (mfaToken) {
              if (channel) {
                setPreferredMfaChannel(channel);
              }
              setMfaToken(mfaToken);
              handleCompleteSignup(null, mfaToken, channel);
            }
          }}
          loginInfo={{
            email: email,
            password: fields.password,
          }}
          phone={userInfo.phone || ''}
          preferredMfa={userInfo.preferredMfa}
          requireMfa={userInfo.companyRequiresMfa}
          mfaEnabled={userInfo.mfaEnabled}
        />
      )}
    </form>
  );

  if (!validLink) {
    return (
      <>
        <Typography variant="h5" align="center" style={{ padding: 5 }}>
          Not a valid signup link
        </Typography>
        <Link href="/signup" variant="h5" align="center" style={{ padding: 5 }}>
          Sign up here
        </Link>
      </>
    );
  }

  // Render the appropriate step
  switch (signupStep) {
    case 'email':
      return renderEmailStep();
    case 'verify':
      return renderVerifyStep();
    case 'complete':
    default:
      return renderCompleteStep();
  }
};

export default SignUpForm;
